/**
 * LOCKER-522 — minimal client for the Zebra Browser Print agent, the local
 * service Zebra installs on the clerk's computer. It listens on loopback only:
 * HTTPS on 9101 (self-signed certificate the browser must accept once) and
 * HTTP on 9100. Same protocol as Zebra's BrowserPrint-3.x SDK, without
 * shipping that unlicensed script: `text/plain` bodies keep every request a
 * "simple" CORS request, so the agent never has to answer a preflight.
 */

export type BrowserPrintDevice = {
  name?: string
  uid?: string
  connection?: string
  deviceType?: string
  version?: number | string
  provider?: string
  manufacturer?: string
}

export class BrowserPrintUnavailableError extends Error {
  constructor(message = "Zebra Browser Print is not reachable") {
    super(message)
    this.name = "BrowserPrintUnavailableError"
  }
}

export class BrowserPrintNoPrinterError extends Error {
  constructor(message = "Zebra Browser Print has no default printer") {
    super(message)
    this.name = "BrowserPrintNoPrinterError"
  }
}

/**
 * HTTP first. Chrome and Edge treat `http://localhost` as a secure origin, so an
 * HTTPS console may call it, and it needs no certificate. HTTPS 9101 is the
 * fallback for browsers that block it; its self-signed certificate must have
 * been accepted once (QA, LOCKER-522: an unaccepted certificate failed every
 * poll with ERR_CERT_AUTHORITY_INVALID).
 */
export const BROWSER_PRINT_BASE_URLS = [
  "http://localhost:9100/",
  "https://localhost:9101/",
] as const

const REQUEST_TIMEOUT_MS = 3000

let workingBaseUrl: string | undefined

/** Test seam: forget the transport that answered last. */
export const resetBrowserPrintTransport = () => {
  workingBaseUrl = undefined
}

const fetchWithTimeout = async (
  url: string,
  init: RequestInit,
  timeoutMs: number,
) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Tries the last transport that answered, then the others. A network failure
 * (agent not installed / not running, certificate not accepted, browser local
 * network permission denied) is indistinguishable from the page, so all of
 * them surface as one `BrowserPrintUnavailableError`.
 */
const request = async (
  path: string,
  init: RequestInit = {},
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<string> => {
  const bases = workingBaseUrl
    ? [
        workingBaseUrl,
        ...BROWSER_PRINT_BASE_URLS.filter((b) => b !== workingBaseUrl),
      ]
    : [...BROWSER_PRINT_BASE_URLS]
  let lastError: unknown
  for (const base of bases) {
    let res: Response
    try {
      res = await fetchWithTimeout(
        base + path,
        {
          ...init,
          headers: { "Content-Type": "text/plain;charset=UTF-8" },
        },
        timeoutMs,
      )
    } catch (err) {
      lastError = err
      continue
    }
    // The agent answered: an HTTP error is its verdict, not a transport miss.
    workingBaseUrl = base
    const text = await res.text()
    if (!res.ok) {
      throw new Error(text || `Browser Print answered HTTP ${res.status}`)
    }
    return text
  }
  workingBaseUrl = undefined
  throw new BrowserPrintUnavailableError(
    lastError instanceof Error ? lastError.message : undefined,
  )
}

/**
 * The agent answers `/default` either as JSON (recent versions) or as the
 * legacy text block `\n\tname:ZD421\n\tdeviceType:printer\n\t…`. An empty body
 * means no default printer is set.
 */
export const parseBrowserPrintDevice = (
  raw: string,
): BrowserPrintDevice | null => {
  const text = raw?.trim()
  if (!text) return null
  if (text.startsWith("{")) {
    try {
      const device = JSON.parse(text) as BrowserPrintDevice
      return device && (device.uid || device.name) ? device : null
    } catch {
      return null
    }
  }
  const device: Record<string, string> = {}
  for (const line of text.split(/\r?\n/)) {
    const idx = line.indexOf(":")
    if (idx <= 0) continue
    device[line.slice(0, idx).trim()] = line.slice(idx + 1).trim()
  }
  return device.uid || device.name ? (device as BrowserPrintDevice) : null
}

export const getDefaultPrinter = async (): Promise<BrowserPrintDevice | null> =>
  parseBrowserPrintDevice(
    await request("default?type=printer", { method: "GET" }),
  )

export const writeToPrinter = async (
  device: BrowserPrintDevice,
  data: string,
): Promise<void> => {
  await request("write", {
    method: "POST",
    body: JSON.stringify({ device, data }),
  })
}

export const readFromPrinter = async (
  device: BrowserPrintDevice,
): Promise<string> =>
  request("read", { method: "POST", body: JSON.stringify({ device }) })

export type PrinterStatus = {
  isReadyToPrint: boolean
  /** i18n keys, `zebraPrinterError.<code>` */
  errors: string[]
}

/**
 * Parses the `~HQES` host status answer. Offsets per the ZPL programming guide
 * (and Zebra's own BrowserPrint-Zebra helper): error flag at 70, then the
 * media / head / pause nibbles.
 */
export const parseHostStatus = (raw: string): PrinterStatus | null => {
  if (!raw || raw.length < 89) return null
  const errors: string[] = []
  const media = raw.charAt(88)
  const head = raw.charAt(87)
  const pause = raw.charAt(84)
  if (media === "1") errors.push("paperOut")
  if (media === "2") errors.push("ribbonOut")
  if (media === "4") errors.push("headOpen")
  if (media === "8") errors.push("cutterFault")
  if (head === "1") errors.push("headOverTemp")
  if (head === "2") errors.push("motorOverTemp")
  if (head === "4") errors.push("headFault")
  if (head === "8") errors.push("incorrectHead")
  if (pause === "1") errors.push("paused")
  const isReadyToPrint = raw.charAt(70) === "0"
  if (!isReadyToPrint && errors.length === 0) errors.push("unknown")
  return { isReadyToPrint, errors }
}

/**
 * Asks the printer itself. `null` when the connection cannot read back
 * (some drivers and network setups are write-only) — not an error.
 */
export const queryPrinterStatus = async (
  device: BrowserPrintDevice,
): Promise<PrinterStatus | null> => {
  await writeToPrinter(device, "~HQES")
  return parseHostStatus(await readFromPrinter(device))
}
